<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WhatsappCatalogsAPI from 'dashboard/api/whatsappCatalogs';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FlowIssuesDialog from '../whatsappFlows/FlowIssuesDialog.vue';
import CatalogProductsPanel from './CatalogProductsPanel.vue';
import { isCloudInbox } from './catalogUtils';

const props = defineProps({
  initialInboxId: { type: [String, Number], default: '' },
});

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const catalogs = ref([]);
const canManage = ref(false);
const isLoading = ref(false);
const isSyncing = ref(false);
const isCreating = ref(false);
const busyCatalogId = ref(null);
const selectedInboxId = ref(String(props.initialInboxId || ''));
const openCatalogId = ref(null);
const newCatalogName = ref('');
const newCatalogError = ref('');
const pendingAction = ref(null);
// Estado retornado pela Meta apenas após ação explícita (sincronizar/consultar).
const inboxMeta = reactive({});
const settingsBusy = ref(false);

const createRef = ref(null);
const confirmRef = ref(null);
const issuesRef = ref(null);

const cloudInboxes = computed(() => (inboxes.value || []).filter(isCloudInbox));
const inboxOptions = computed(() =>
  cloudInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: getInboxLabel(inbox),
  }))
);
const visibleCatalogs = computed(() =>
  catalogs.value.filter(
    catalog => String(catalog.inbox_id) === String(selectedInboxId.value)
  )
);
const openCatalog = computed(() =>
  visibleCatalogs.value.find(catalog => catalog.id === openCatalogId.value)
);
const currentMeta = computed(() => inboxMeta[selectedInboxId.value] || {});
const commerceSettings = computed(() => currentMeta.value.commerceSettings);
const hasConnectedCatalog = computed(() =>
  visibleCatalogs.value.some(catalog => catalog.connected)
);
const lastSyncedAt = computed(() =>
  visibleCatalogs.value
    .map(catalog => catalog.last_synced_at)
    .filter(Boolean)
    .sort()
    .pop()
);

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});
const formatDate = value =>
  value ? dateFormatter.format(new Date(value)) : '';

const errorMessage = (error, fallback = 'WHATSAPP_CATALOGS.ACTION_ERROR') =>
  error?.response?.data?.error || t(fallback);

const showIssues = (title, issues) =>
  issuesRef.value?.open({
    title,
    issues,
    confirmLabel: t('WHATSAPP_CATALOGS.ISSUES.CLOSE'),
  });

const showError = (titleKey, error, fallback) =>
  showIssues(t(titleKey), [errorMessage(error, fallback)]);

const replaceCatalog = updated => {
  if (!updated?.id) return;
  const index = catalogs.value.findIndex(item => item.id === updated.id);
  if (index >= 0) catalogs.value.splice(index, 1, updated);
  else catalogs.value.unshift(updated);
};

const fetchCatalogs = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.list();
    catalogs.value = data.payload || [];
    canManage.value = !!data.can_manage;
  } catch (error) {
    showError(
      'WHATSAPP_CATALOGS.ISSUES.LOAD_ERROR_TITLE',
      error,
      'WHATSAPP_CATALOGS.LOAD_ERROR'
    );
  } finally {
    isLoading.value = false;
  }
};

const syncCatalogs = async () => {
  const inboxId = selectedInboxId.value;
  if (!inboxId || isSyncing.value) return;
  isSyncing.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.sync(inboxId);
    catalogs.value = [
      ...catalogs.value.filter(item => String(item.inbox_id) !== inboxId),
      ...(data.payload || []),
    ];
    inboxMeta[inboxId] = {
      business: data.business,
      commerceSettings: data.commerce_settings,
    };
    if (data.warnings?.length) {
      showIssues(
        t('WHATSAPP_CATALOGS.ISSUES.SYNC_WARNING_TITLE'),
        data.warnings
      );
    } else {
      useAlert(t('WHATSAPP_CATALOGS.SYNC_SUCCESS'));
    }
  } catch (error) {
    showError('WHATSAPP_CATALOGS.ISSUES.SYNC_ERROR_TITLE', error);
  } finally {
    isSyncing.value = false;
  }
};

const openCreate = () => {
  newCatalogName.value = '';
  newCatalogError.value = '';
  createRef.value?.open();
};

const createCatalog = async () => {
  const name = newCatalogName.value.trim();
  if (!name || name.length > 200) {
    newCatalogError.value = t('WHATSAPP_CATALOGS.CREATE.NAME_ERROR');
    return;
  }
  if (isCreating.value) return;
  isCreating.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.createCatalog(
      selectedInboxId.value,
      name
    );
    replaceCatalog(data);
    createRef.value?.close();
    if (data.warning) {
      showIssues(t('WHATSAPP_CATALOGS.CREATE.WARNING_TITLE'), [data.warning]);
    } else {
      useAlert(t('WHATSAPP_CATALOGS.CREATE.SUCCESS'));
    }
  } catch (error) {
    showError('WHATSAPP_CATALOGS.ISSUES.ACTION_ERROR_TITLE', error);
  } finally {
    isCreating.value = false;
  }
};

const confirmCopy = computed(() => {
  const action = pendingAction.value;
  if (!action) return {};
  const key = action.type === 'connect' ? 'CONNECT' : 'DISCONNECT';
  return {
    title: t(`WHATSAPP_CATALOGS.${key}.TITLE`),
    description: t(`WHATSAPP_CATALOGS.${key}.DESCRIPTION`, {
      name: action.catalog.name,
    }),
    confirm: t(`WHATSAPP_CATALOGS.${key}.CONFIRM`),
  };
});

const askAction = (type, catalog) => {
  pendingAction.value = { type, catalog };
  confirmRef.value?.open();
};

const runPendingAction = async () => {
  const action = pendingAction.value;
  if (!action || busyCatalogId.value) return;
  busyCatalogId.value = action.catalog.id;
  try {
    const { data } = await WhatsappCatalogsAPI[action.type](action.catalog.id);
    replaceCatalog(data);
    useAlert(
      t(
        action.type === 'connect'
          ? 'WHATSAPP_CATALOGS.CONNECT.SUCCESS'
          : 'WHATSAPP_CATALOGS.DISCONNECT.SUCCESS'
      )
    );
    confirmRef.value?.close();
    pendingAction.value = null;
  } catch (error) {
    confirmRef.value?.close();
    showError('WHATSAPP_CATALOGS.ISSUES.ACTION_ERROR_TITLE', error);
  } finally {
    busyCatalogId.value = null;
  }
};

const checkCommerceSettings = async () => {
  const inboxId = selectedInboxId.value;
  if (!inboxId || settingsBusy.value) return;
  settingsBusy.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.commerceSettings(inboxId);
    inboxMeta[inboxId] = { ...inboxMeta[inboxId], commerceSettings: data };
  } catch (error) {
    showError('WHATSAPP_CATALOGS.ISSUES.ACTION_ERROR_TITLE', error);
  } finally {
    settingsBusy.value = false;
  }
};

const updateSetting = async (field, value) => {
  const inboxId = selectedInboxId.value;
  const current = commerceSettings.value;
  if (!inboxId || !current || settingsBusy.value) return;
  settingsBusy.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.updateCommerceSettings(inboxId, {
      ...current,
      [field]: value,
    });
    inboxMeta[inboxId] = { ...inboxMeta[inboxId], commerceSettings: data };
    useAlert(t('WHATSAPP_CATALOGS.VISIBILITY.SUCCESS'));
  } catch (error) {
    showError('WHATSAPP_CATALOGS.ISSUES.ACTION_ERROR_TITLE', error);
  } finally {
    settingsBusy.value = false;
  }
};

const copyCatalogId = async catalog => {
  await copyTextToClipboard(catalog.meta_catalog_id);
  useAlert(t('WHATSAPP_CATALOGS.ACTIONS.ID_COPIED'));
};

const toggleProducts = catalog => {
  openCatalogId.value = openCatalogId.value === catalog.id ? null : catalog.id;
};

const ensureSelectedInbox = () => {
  const ids = inboxOptions.value.map(option => option.value);
  if (!ids.includes(selectedInboxId.value))
    selectedInboxId.value = ids[0] || '';
};

watch(inboxOptions, ensureSelectedInbox);
watch(selectedInboxId, () => {
  openCatalogId.value = null;
});
watch(
  () => props.initialInboxId,
  value => {
    if (value) selectedInboxId.value = String(value);
  }
);

onMounted(async () => {
  ensureSelectedInbox();
  await fetchCatalogs();
});

defineExpose({ fetchCatalogs, openCreate });
</script>

<template>
  <section class="flex flex-col gap-4">
    <p
      v-if="!cloudInboxes.length"
      class="mb-0 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
    >
      {{ t('WHATSAPP_CATALOGS.NO_CLOUD_INBOX') }}
    </p>

    <template v-else>
      <div
        class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between"
      >
        <label
          class="mb-0 flex min-w-0 flex-col gap-1.5 text-sm font-medium text-n-slate-12 md:min-w-72"
        >
          {{ t('WHATSAPP_CATALOGS.INBOX') }}
          <NextSelect
            v-model="selectedInboxId"
            :options="inboxOptions"
            class="w-full [&>select]:w-full"
          />
        </label>
        <div v-if="canManage" class="flex flex-wrap gap-2">
          <NextButton
            type="button"
            variant="faded"
            color="slate"
            size="sm"
            icon="i-hugeicons-refresh"
            :label="t('WHATSAPP_CATALOGS.SYNC')"
            :title="t('WHATSAPP_CATALOGS.SYNC_HELP')"
            :disabled="!selectedInboxId"
            :is-loading="isSyncing"
            @click="syncCatalogs"
          />
          <NextButton
            type="button"
            size="sm"
            icon="i-hugeicons-add-01"
            :label="t('WHATSAPP_CATALOGS.NEW')"
            :disabled="!selectedInboxId"
            @click="openCreate"
          />
        </div>
      </div>

      <p
        v-if="!canManage && !isLoading"
        class="mb-0 rounded-xl bg-n-slate-3 p-3 text-sm text-n-slate-12"
      >
        {{ t('WHATSAPP_CATALOGS.READ_ONLY') }}
      </p>

      <div
        class="flex flex-col gap-1 text-xs text-n-slate-11 sm:flex-row sm:flex-wrap sm:gap-x-4"
      >
        <span>
          {{
            lastSyncedAt
              ? t('WHATSAPP_CATALOGS.LAST_SYNC', {
                  date: formatDate(lastSyncedAt),
                })
              : t('WHATSAPP_CATALOGS.NEVER_SYNCED')
          }}
        </span>
        <span v-if="currentMeta.business?.name">
          {{
            t('WHATSAPP_CATALOGS.BUSINESS', {
              name: currentMeta.business.name,
            })
          }}
        </span>
      </div>

      <div
        v-if="canManage"
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div
          class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="min-w-0">
            <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ t('WHATSAPP_CATALOGS.VISIBILITY.TITLE') }}
            </h3>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ t('WHATSAPP_CATALOGS.VISIBILITY.DESCRIPTION') }}
            </p>
          </div>
          <NextButton
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-view"
            :label="t('WHATSAPP_CATALOGS.VISIBILITY.CHECK')"
            :is-loading="settingsBusy && !commerceSettings"
            @click="checkCommerceSettings"
          />
        </div>
        <p v-if="!commerceSettings" class="mb-0 text-sm text-n-slate-11">
          {{ t('WHATSAPP_CATALOGS.VISIBILITY.UNKNOWN') }}
        </p>
        <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div
            v-for="setting in [
              {
                field: 'is_catalog_visible',
                label: t('WHATSAPP_CATALOGS.VISIBILITY.VISIBLE'),
                help: t('WHATSAPP_CATALOGS.VISIBILITY.VISIBLE_HELP'),
              },
              {
                field: 'is_cart_enabled',
                label: t('WHATSAPP_CATALOGS.VISIBILITY.CART'),
                help: t('WHATSAPP_CATALOGS.VISIBILITY.CART_HELP'),
              },
            ]"
            :key="setting.field"
            class="flex items-start justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <div class="min-w-0">
              <p class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ setting.label }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">{{ setting.help }}</p>
            </div>
            <Switch
              :model-value="!!commerceSettings[setting.field]"
              :aria-label="setting.label"
              :class="{ 'pointer-events-none opacity-60': settingsBusy }"
              @update:model-value="value => updateSetting(setting.field, value)"
            />
          </div>
          <p
            v-if="!hasConnectedCatalog"
            class="mb-0 text-xs text-n-amber-11 md:col-span-2"
          >
            {{ t('WHATSAPP_CATALOGS.VISIBILITY.NEEDS_CATALOG') }}
          </p>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex min-h-40 items-center justify-center text-sm text-n-slate-11"
        role="status"
      >
        {{ t('WHATSAPP_CATALOGS.LOADING') }}
      </div>

      <div
        v-else-if="visibleCatalogs.length"
        class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <article
          v-for="catalog in visibleCatalogs"
          :key="catalog.id"
          class="flex min-w-0 flex-col gap-3 rounded-xl border bg-n-solid-1 p-4"
          :class="
            openCatalogId === catalog.id ? 'border-n-brand' : 'border-n-weak'
          "
        >
          <header class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-12"
                aria-hidden="true"
              >
                <span class="i-hugeicons-store-01 size-4" />
              </span>
              <div class="min-w-0">
                <h4
                  class="mb-1 truncate text-sm font-semibold text-n-slate-12"
                  :title="catalog.name"
                >
                  {{ catalog.name }}
                </h4>
                <p class="mb-0 truncate text-xs text-n-slate-11">
                  {{
                    t('WHATSAPP_CATALOGS.CATALOG_ID', {
                      id: catalog.meta_catalog_id,
                    })
                  }}
                </p>
              </div>
            </div>
            <span
              class="shrink-0 rounded-md px-2 py-1 text-xs font-medium"
              :class="
                catalog.connected
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                catalog.connected
                  ? t('WHATSAPP_CATALOGS.STATUS.CONNECTED')
                  : t('WHATSAPP_CATALOGS.STATUS.DISCONNECTED')
              }}
            </span>
          </header>
          <p class="mb-0 flex items-center gap-1.5 text-xs text-n-slate-11">
            <span class="i-hugeicons-package size-4 shrink-0" />
            {{
              catalog.product_count === null ||
              catalog.product_count === undefined
                ? t('WHATSAPP_CATALOGS.PRODUCT_COUNT_UNKNOWN')
                : t('WHATSAPP_CATALOGS.PRODUCT_COUNT', {
                    count: catalog.product_count,
                  })
            }}
          </p>
          <footer
            class="mt-auto flex flex-wrap gap-1 border-t border-n-weak pt-3"
          >
            <NextButton
              v-if="canManage"
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-shopping-bag-01"
              :label="t('WHATSAPP_CATALOGS.ACTIONS.PRODUCTS')"
              :aria-expanded="openCatalogId === catalog.id"
              @click="toggleProducts(catalog)"
            />
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-copy-01"
              :aria-label="t('WHATSAPP_CATALOGS.ACTIONS.COPY_ID')"
              :title="t('WHATSAPP_CATALOGS.ACTIONS.COPY_ID')"
              @click="copyCatalogId(catalog)"
            />
            <template v-if="canManage">
              <NextButton
                v-if="catalog.connected"
                type="button"
                size="xs"
                variant="ghost"
                color="ruby"
                icon="i-hugeicons-unlink-02"
                :label="t('WHATSAPP_CATALOGS.ACTIONS.DISCONNECT')"
                :is-loading="busyCatalogId === catalog.id"
                @click="askAction('disconnect', catalog)"
              />
              <NextButton
                v-else
                type="button"
                size="xs"
                variant="ghost"
                color="teal"
                icon="i-hugeicons-link-02"
                :label="t('WHATSAPP_CATALOGS.ACTIONS.CONNECT')"
                :is-loading="busyCatalogId === catalog.id"
                @click="askAction('connect', catalog)"
              />
            </template>
          </footer>
        </article>
      </div>

      <div
        v-else
        class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-strong bg-n-solid-1 p-6 text-center"
      >
        <span
          class="grid size-12 place-items-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-hugeicons-store-01 size-6" />
        </span>
        <h4 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('WHATSAPP_CATALOGS.EMPTY_TITLE') }}
        </h4>
        <p class="mb-0 max-w-md text-sm text-n-slate-11">
          {{ t('WHATSAPP_CATALOGS.EMPTY_DESCRIPTION') }}
        </p>
        <div v-if="canManage" class="flex flex-wrap justify-center gap-2">
          <NextButton
            type="button"
            variant="faded"
            color="slate"
            icon="i-hugeicons-refresh"
            :label="t('WHATSAPP_CATALOGS.SYNC')"
            :is-loading="isSyncing"
            @click="syncCatalogs"
          />
          <NextButton
            type="button"
            icon="i-hugeicons-add-01"
            :label="t('WHATSAPP_CATALOGS.NEW')"
            @click="openCreate"
          />
        </div>
      </div>

      <CatalogProductsPanel
        v-if="openCatalog && canManage"
        :key="openCatalog.id"
        :catalog="openCatalog"
        @catalog-updated="replaceCatalog"
        @issues="({ title, issues }) => showIssues(title, issues)"
        @close="openCatalogId = null"
      />
    </template>

    <Dialog
      ref="createRef"
      type="edit"
      solid
      :title="t('WHATSAPP_CATALOGS.CREATE.TITLE')"
      :description="t('WHATSAPP_CATALOGS.CREATE.DESCRIPTION')"
      :confirm-button-label="t('WHATSAPP_CATALOGS.CREATE.CONFIRM')"
      :is-loading="isCreating"
      @confirm="createCatalog"
    >
      <Input
        v-model="newCatalogName"
        :label="t('WHATSAPP_CATALOGS.CREATE.NAME')"
        :placeholder="t('WHATSAPP_CATALOGS.CREATE.NAME_PLACEHOLDER')"
        :message="newCatalogError"
        :message-type="newCatalogError ? 'error' : 'info'"
        autofocus
      />
    </Dialog>

    <Dialog
      ref="confirmRef"
      type="alert"
      :title="confirmCopy.title"
      :description="confirmCopy.description"
      :confirm-button-label="confirmCopy.confirm"
      :is-loading="!!busyCatalogId"
      @confirm="runPendingAction"
    />
    <FlowIssuesDialog ref="issuesRef" />
  </section>
</template>
